import { FilePlus2, Loader2, RefreshCw } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { SettingsWrapper } from './SettingsWrapper';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Button } from '@/components/atoms/new-button';
import { Textarea } from '@/components/atoms/textarea';

interface CaseDetailsProps {
  isLoading: boolean;
  caseIssues: string;
  handleIssuesChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  caseParties: string;
  handlePartiesChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  caseContext: string;
  handleContextChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  caseTimePeriod: string;
  handleTimePeriodChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  setConfirmationModalOpen: (open: boolean) => void;
  detailsChanged: boolean;
  isReprocessing: boolean;
}

export const CaseDetails = ({
  isLoading,
  caseIssues,
  handleIssuesChange,
  caseParties,
  handlePartiesChange,
  caseContext,
  handleContextChange,
  caseTimePeriod,
  handleTimePeriodChange,
  setConfirmationModalOpen,
  detailsChanged,
  isReprocessing,
}: CaseDetailsProps) => {
  const [searchParams] = useSearchParams();
  const caseId = searchParams.get('caseId');
  const navigate = useNavigate();

  return (
    <SettingsWrapper>
      <div>
        <div>
          <h2 className="mb-2 text-base font-semibold text-gray-900">Case Details</h2>
          <p className="mb-4 text-sm text-gray-500">
            Update your case details to amend the 'significance' of each fact to the case issues, as well as regenerate
            case summary and themes.
          </p>
        </div>

        {isLoading ? (
          <div className="flex h-72 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="caseIssues" className="text-sm font-medium text-gray-700">
                Case Issues
              </Label>
              <Textarea
                id="caseIssues"
                className="min-h-36 resize-none"
                value={caseIssues}
                onChange={handleIssuesChange}
                placeholder="Enter the main legal issues for this case..."
                disabled={isReprocessing}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="caseParties" className="text-sm font-medium text-gray-700">
                Case Entities
              </Label>
              <Input
                id="caseParties"
                value={caseParties}
                onChange={handlePartiesChange}
                placeholder="Enter the parties involved in this case..."
                disabled={isReprocessing}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="caseContext" className="text-sm font-medium text-gray-700">
                Additional Context (optional)
              </Label>
              <Textarea
                id="caseContext"
                className="min-h-20 resize-none"
                value={caseContext}
                onChange={handleContextChange}
                placeholder="Enter any additional context that might be relevant..."
                disabled={isReprocessing}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="caseTimePeriod" className="text-sm font-medium text-gray-700">
                Estimated Time Period (optional)
              </Label>
              <Input
                id="caseTimePeriod"
                value={caseTimePeriod}
                onChange={handleTimePeriodChange}
                placeholder="e.g., 2020-2023, Q1 2022, etc."
                disabled={isReprocessing}
              />
            </div>
          </div>
        )}
      </div>
      <div className="mt-6 border-t border-gray-200 pt-4">
        <div className="flex gap-3">
          <Button
            variant="brand"
            size="sm"
            onClick={() => setConfirmationModalOpen(true)}
            disabled={!detailsChanged || isReprocessing}
          >
            <RefreshCw className="h-4 w-4" />
            Update Case
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate(`/app/chronos/case-editor/settings/documents?caseId=${caseId}`)}
            disabled={isReprocessing}
          >
            <FilePlus2 className="h-4 w-4" />
            Add Documents
          </Button>
        </div>
        {isReprocessing ? (
          <p className="mt-2 text-xs text-amber-600">
            Case is currently being processed. Updates are temporarily disabled.
          </p>
        ) : (
          <p className="mt-2 text-xs text-gray-500">
            Update just your case details, or add documents first then update everything together.
          </p>
        )}
      </div>
    </SettingsWrapper>
  );
};
